import { useEffect } from "react";
import { useLocation } from "react-router";

import type { FactoriesFactory } from "@/api-client";
import { PermissionsProvider } from "@/contexts/PermissionsProvider";
import { useAccount } from "@/contexts/useAccount";
import { usePermissions } from "@/contexts/usePermissions";
import { useFactories, useFactory } from "@/hooks/useFactoryData";
import { useRecordLastLocation } from "@/hooks/useRecordLastLocation";
import { recordLastVisitedOrganization } from "@/lib/lastVisitedOrganization";

import { factoryRouteSegment, resolveFactoryByKey } from "../../lib/factoryKeyResolution";
import { useFactoriesThemeClass } from "../../lib/useFactoriesThemeClass";
import { FactoriesLayoutError, FactoriesLayoutLoading } from "../../layout/FactoriesLayout";
import { FactoriesLayoutContext } from "../../layout/factoriesLayoutContext";
import { OnboardingPage } from "./OnboardingPage";
import { onboardingResumePath } from "./onboardingResumePath";

const ignoreOpenCreateWorkOrder = () => undefined;

/** Renders the existing workspace wizard while the provisional organization stays outside the browser URL. */
export function InitialWorkspaceOnboarding({
  organizationId,
  factoryKey,
}: {
  organizationId: string;
  factoryKey: string;
}) {
  useFactoriesThemeClass();
  const factories = useFactories(organizationId);
  const resolution = resolveFactoryByKey(factories.data ?? [], factoryKey, factories.isLoading || factories.isFetching);

  if (factories.isError || resolution.status === "not-found") {
    return <FactoriesLayoutError organizationId={organizationId} />;
  }
  if (resolution.status === "loading" || !resolution.factory?.id) {
    return <FactoriesLayoutLoading />;
  }

  return (
    <PermissionsProvider organizationId={organizationId}>
      <ResolvedInitialWorkspaceOnboarding
        organizationId={organizationId}
        factoryId={resolution.factory.id}
        factoryKey={factoryRouteSegment(resolution.factory) || factoryKey}
        factories={factories.data ?? []}
      />
    </PermissionsProvider>
  );
}

function ResolvedInitialWorkspaceOnboarding({
  organizationId,
  factoryId,
  factoryKey,
  factories,
}: {
  organizationId: string;
  factoryId: string;
  factoryKey: string;
  factories: FactoriesFactory[];
}) {
  const factory = useFactory(organizationId, factoryId);
  const permissions = usePermissions();

  if (factory.isError) {
    return <FactoriesLayoutError organizationId={organizationId} />;
  }
  if (factory.isLoading || permissions.isLoading || !factory.data) {
    return <FactoriesLayoutLoading />;
  }

  return (
    <FactoriesLayoutContext.Provider
      value={{
        organizationId,
        factoryId,
        factoryKey: factory.data.key ?? factoryKey,
        routeSegment: factoryRouteSegment(factory.data) || factoryKey,
        factory: factory.data,
        factories,
        openCreateWorkOrder: ignoreOpenCreateWorkOrder,
      }}
    >
      <RecordOnboardingLastLocation
        organizationSlug={organizationId}
        factoryKey={factoryRouteSegment(factory.data) || factoryKey}
      />
      <main className="h-dvh overflow-y-auto bg-background">
        <OnboardingPage />
      </main>
    </FactoriesLayoutContext.Provider>
  );
}

function RecordOnboardingLastLocation({
  organizationSlug,
  factoryKey,
}: {
  organizationSlug: string;
  factoryKey: string;
}) {
  const { account } = useAccount();
  const { search } = useLocation();
  const { canAct, isLoading } = usePermissions();
  const resumePath = onboardingResumePath(organizationSlug, factoryKey, search);
  const canRecord = !isLoading && (canAct("factories", "read") || canAct("canvases", "read"));

  useEffect(() => {
    if (!canRecord || !account?.id) {
      return;
    }
    recordLastVisitedOrganization(account.id, organizationSlug);
  }, [account?.id, canRecord, organizationSlug]);

  useRecordLastLocation(canRecord ? organizationSlug : null, account?.id, resumePath);
  return null;
}
